import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { useMediaQuery } from '@material-ui/core';
import { noop } from 'lodash';

import useDebounced from './UseDebounced';

function getSize(elRef) {
    if (!elRef?.current) {
        return { width: 0, height: 0 };
    }

    const { clientWidth: width, clientHeight: height } = elRef.current;

    return { width, height };
}

export default function useSize(elRef, debounceDelay = 20) {
    const [size, setSize] = useState(getSize(elRef));
    const { width, height } = size;
    const isPrintMedia = useMediaQuery('@media print');

    const setSizeRef = useRef();
    setSizeRef.current = setSize;

    const resizeHandler = useCallback(() => {
        const { width: newWidth, height: newHeight } = getSize(elRef);

        if (width !== newWidth || height !== newHeight) {
            setSizeRef.current({ width: newWidth, height: newHeight });
        }
    }, [elRef, width, height]);

    // Handle normal asynchronous resize events
    const debouncedResizeHandler = useDebounced(resizeHandler, debounceDelay);

    // When we unmount this component, we need to make sure we are no longer making
    // calls to setSize. For some reason useResizeObserver does not do this correctly
    // and calls the callback _after_ we've already unmounted, which React complains
    // is a memory leak (which is accurate). This wipes out the stateful callback
    // and prevents that error and does nothing instead.
    useEffect(() => {
        return () => {
            setSizeRef.current = noop;
        };
    }, []);

    // TODO - remove useResizeObserver. At this point we've probably got more
    // code written to catch its holes/bugs than it would be to write a proper
    // implementation.
    useResizeObserver({ onResize: debouncedResizeHandler, ref: elRef });

    // This may seem redundant to the line above where we use the useResizeObserver hook
    // but it doesn't seem to catch some resize events. I'm not entirely sure why this is.
    // We need to look for for height/width changes on hook calls too, so we can properly
    // notify elements that need their size when things change. Note: this code here
    // doesn't catch all cases (i.e., browser resizes), so we do stil unfortunately need
    // useResizeObserver until we can probably roll our own proper size management code.
    useLayoutEffect(() => {
        if (height > 0 || width > 0) {
            debouncedResizeHandler();
        }
    }, [debouncedResizeHandler, height, width]);

    // Synchronously update the size when printing
    useLayoutEffect(() => {
        if (isPrintMedia) {
            resizeHandler();
        }
    }, [isPrintMedia, resizeHandler]);

    const { width: currentWidth } = getSize(elRef);

    useLayoutEffect(() => {
        if (width !== currentWidth) {
            setSize({ height, width: currentWidth });
        }
    }, [height, width, currentWidth]);

    return size;
}
